import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Close,
  Delete,
  Edit,
  Search,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import { StatusChip, segmentedToggleSx } from './advertisingUi';
import {
  BREAK_DAY_SHORT,
  dayLabel,
  filterBreaks,
  formatBreakTime,
  groupBreaksByHour,
  isBreakActive,
  sortBreaks,
  summariseBreaks,
} from '../utils/advertisingBreaks';

const COLUMNS = [
  { id: 'station', label: 'Station' },
  { id: 'name', label: 'Name' },
  { id: 'time', label: 'Time' },
  { id: 'max_duration', label: 'Duration' },
  { id: 'max_spots', label: 'Max Spots' },
  { id: 'priority', label: 'Priority' },
  { id: 'is_active', label: 'Status' },
  { id: 'actions', label: 'Actions', sortable: false },
];

const searchFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'var(--rs-overlay-soft)',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
  },
};

const DayChip = ({ value }) => (
  <Chip
    size="small"
    label={value === null || value === undefined || value === '' ? 'All Days' : BREAK_DAY_SHORT[Number(value)]}
    sx={{ bgcolor: 'var(--rs-border)', color: 'var(--rs-muted)' }}
  />
);

export default function CommercialBreaksPanel({
  breaks = [],
  stations = [],
  canEdit = false,
  onEdit,
  onDelete,
  onCreate,
}) {
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'hour'
  const [sort, setSort] = useState({ orderBy: 'time', order: 'asc' });
  const [search, setSearch] = useState('');
  const [stationFilter, setStationFilter] = useState('');
  const [dayFilter, setDayFilter] = useState('any');
  const [statusFilter, setStatusFilter] = useState('any');

  const stationNames = useMemo(
    () => Object.fromEntries(stations.map((station) => [String(station.id), station.name])),
    [stations],
  );

  const filtered = useMemo(
    () => filterBreaks(breaks, { search, stationId: stationFilter, day: dayFilter, status: statusFilter }, stationNames),
    [breaks, search, stationFilter, dayFilter, statusFilter, stationNames],
  );

  const sorted = useMemo(() => sortBreaks(filtered, sort, stationNames), [filtered, sort, stationNames]);
  const groups = useMemo(() => groupBreaksByHour(sorted, stationNames), [sorted, stationNames]);
  const summary = useMemo(() => summariseBreaks(filtered), [filtered]);

  const hasFilters = Boolean(search || stationFilter || dayFilter !== 'any' || statusFilter !== 'any');
  const clearFilters = () => {
    setSearch('');
    setStationFilter('');
    setDayFilter('any');
    setStatusFilter('any');
  };

  // A single-station install doesn't need a column repeating the same name.
  const showStation = stations.length > 1;

  const handleSort = (field) => {
    setSort((prev) => ({
      orderBy: field,
      order: prev.orderBy === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderActions = (breakItem) => {
    const locked = !canEdit || breakItem.is_syndicated;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={breakItem.is_syndicated ? 'Managed by the parent station (read-only)' : 'Edit break'}>
          <span>
            <IconButton
              size="small"
              disabled={locked}
              onClick={() => onEdit?.(breakItem)}
              sx={{ color: '#22d3ee' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={breakItem.is_syndicated ? 'Managed by the parent station (read-only)' : 'Delete break'}>
          <span>
            <IconButton
              size="small"
              disabled={locked}
              onClick={() => onDelete?.(breakItem)}
              sx={{ color: '#ff6b6b' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const emptyRow = (
    <TableRow>
      <TableCell colSpan={showStation ? 8 : 7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
        <Typography color="text.secondary">
          {breaks.length === 0
            ? 'No commercial breaks configured. Create your first break to get started.'
            : 'No breaks match these filters.'}
        </Typography>
        {hasFilters && (
          <Button variant="outlined" size="small" onClick={clearFilters} sx={{ mt: 1.5 }}>
            Clear filters
          </Button>
        )}
        {breaks.length === 0 && canEdit && (
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => onCreate?.()} sx={{ mt: 1.5, ml: 1 }}>
            New Break
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {breaks.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Breaks', value: summary.total, color: '#22d3ee' },
            { label: 'Active', value: summary.active, color: summary.inactive > 0 ? '#f59e0b' : '#22c55e' },
            { label: 'Stations Covered', value: summary.stationCount, color: '#22c55e' },
            { label: 'Avg Duration', value: `${summary.averageDuration}s`, color: '#f59e0b' },
            { label: 'Spot Capacity', value: summary.spotCapacity, color: '#22d3ee' },
          ].map((stat) => (
            <Card key={stat.label} sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                  {stat.label}
                </Typography>
                <Typography variant="h5" className="tabular-nums" sx={{ color: stat.color, fontWeight: 700 }}>
                  {stat.value}
                </Typography>
                {stat.label === 'Active' && summary.inactive > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {summary.inactive} inactive
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by name, station or time..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                endAdornment: search ? (
                  <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                    <Close fontSize="small" />
                  </IconButton>
                ) : null,
              }}
              sx={{ ...searchFieldSx, flexGrow: 1, minWidth: 220 }}
            />
            {stations.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Station</InputLabel>
                <Select value={stationFilter} onChange={(e) => setStationFilter(e.target.value)} label="Station">
                  <MenuItem value="">All Stations</MenuItem>
                  {stations.map((station) => (
                    <MenuItem key={station.id} value={String(station.id)}>{station.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Day</InputLabel>
              <Select value={dayFilter} onChange={(e) => setDayFilter(e.target.value)} label="Day">
                <MenuItem value="any">Any Day</MenuItem>
                <MenuItem value="every">Every Day</MenuItem>
                {BREAK_DAY_SHORT.map((short, index) => (
                  <MenuItem key={short} value={String(index)}>{dayLabel(index)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="any">Any Status</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(e, value) => value && setViewMode(value)}
              sx={segmentedToggleSx}
            >
              <ToggleButton value="list" aria-label="list view">
                <ViewList sx={{ mr: 0.5 }} fontSize="small" />
                List
              </ToggleButton>
              <ToggleButton value="hour" aria-label="grouped by hour">
                <ViewModule sx={{ mr: 0.5 }} fontSize="small" />
                By Hour
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === 'list' ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((column) => {
                      if (column.id === 'station' && !showStation) return null;
                      const active = sort.orderBy === column.id;
                      return (
                        <TableCell
                          key={column.id}
                          align={column.id === 'actions' ? 'right' : 'left'}
                          sortDirection={active ? sort.order : false}
                        >
                          {column.sortable === false ? column.label : (
                            <TableSortLabel
                              active={active}
                              direction={active ? sort.order : 'asc'}
                              onClick={() => handleSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 ? emptyRow : sorted.map((breakItem) => (
                    <TableRow key={breakItem.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                      {showStation && (
                        <TableCell>{stationNames[String(breakItem.station_id)] || breakItem.station_id}</TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2">{breakItem.name}</Typography>
                          {breakItem.is_syndicated && (
                            <Chip
                              size="small"
                              label="Mandatory"
                              sx={{ bgcolor: 'rgba(192,132,252,0.12)', color: '#d8b4fe' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="tabular-nums">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {formatBreakTime(breakItem.hour, breakItem.minute)}
                          <DayChip value={breakItem.day_of_week} />
                        </Box>
                      </TableCell>
                      <TableCell className="tabular-nums">{breakItem.max_duration}s</TableCell>
                      <TableCell className="tabular-nums">{breakItem.max_spots}</TableCell>
                      <TableCell className="tabular-nums">{breakItem.priority}</TableCell>
                      <TableCell>
                        <StatusChip
                          status={isBreakActive(breakItem) ? 'active' : 'inactive'}
                          label={isBreakActive(breakItem) ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell align="right">{renderActions(breakItem)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : groups.length === 0 ? (
            <TableContainer>
              <Table size="small"><TableBody>{emptyRow}</TableBody></Table>
            </TableContainer>
          ) : (
            <Box>
              {groups.map((group) => (
                <Box key={group.key} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Typography variant="subtitle2">
                      {group.stationName} · {String(group.hour).padStart(2, '0')}:00
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.spotCount} {group.spotCount === 1 ? 'break' : 'breaks'}
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {group.breaks.map((breakItem) => (
                        <TableRow key={breakItem.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                          <TableCell className="tabular-nums" sx={{ width: 90 }}>
                            {formatBreakTime(breakItem.hour, breakItem.minute)}
                          </TableCell>
                          <TableCell>{breakItem.name}</TableCell>
                          <TableCell sx={{ width: 120 }}><DayChip value={breakItem.day_of_week} /></TableCell>
                          <TableCell className="tabular-nums" sx={{ width: 90 }}>{breakItem.max_duration}s</TableCell>
                          <TableCell className="tabular-nums" sx={{ width: 90 }}>{breakItem.max_spots}</TableCell>
                          <TableCell sx={{ width: 110 }}>
                            <StatusChip
                              status={isBreakActive(breakItem) ? 'active' : 'inactive'}
                              label={isBreakActive(breakItem) ? 'Active' : 'Inactive'}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 96 }}>{renderActions(breakItem)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
